import React, { useState } from 'react';
import { useSwipePreferences, SwipeActionType } from '../../contexts/SwipePreferencesContext';

interface SwipePreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SwipePreferencesModal: React.FC<SwipePreferencesModalProps> = ({ isOpen, onClose }) => {
  const { preferences, updatePreferences, availableActions } = useSwipePreferences();
  const [tempPreferences, setTempPreferences] = useState(preferences);

  if (!isOpen) return null;

  const handleSave = () => {
    updatePreferences(tempPreferences);
    onClose();
  };

  const handleCancel = () => {
    setTempPreferences(preferences);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900/95 border border-emerald-800/30 rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-emerald-300">
            Raccourcis de glissement
          </h2>
          <button
            onClick={handleCancel}
            className="text-emerald-300/60 hover:text-emerald-300 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Activer/Désactiver les gestes */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-emerald-300 font-medium">Activer les gestes</h3>
              <p className="text-emerald-300/60 text-sm">Glissez sur les posts pour des actions rapides</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={tempPreferences.isEnabled}
                onChange={(e) => setTempPreferences(prev => ({ ...prev, isEnabled: e.target.checked }))}
              />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-800/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
            </label>
          </div>
        </div>

        {tempPreferences.isEnabled && (
          <>
            {/* Action pour glissement vers la gauche */}
            <div className="mb-6">
              <h3 className="text-emerald-300 font-medium mb-3">Glissement vers la gauche 👈</h3>
              <div className="grid grid-cols-2 gap-2">
                {availableActions.map((action) => (
                  <button
                    key={action.type}
                    onClick={() => setTempPreferences(prev => ({ ...prev, leftAction: action.type }))}
                    className={`p-3 rounded-lg border transition-all ${
                      tempPreferences.leftAction === action.type
                        ? 'border-emerald-600 bg-emerald-600/20 text-emerald-300'
                        : 'border-gray-700 hover:border-emerald-800 text-emerald-300/80 hover:text-emerald-300'
                    }`}
                  >
                    <div className="text-center">
                      <div className="text-2xl mb-1">{action.icon}</div>
                      <div className="text-xs">{action.label}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Action pour glissement vers la droite */}
            <div className="mb-6">
              <h3 className="text-emerald-300 font-medium mb-3">Glissement vers la droite 👉</h3>
              <div className="grid grid-cols-2 gap-2">
                {availableActions.map((action) => (
                  <button
                    key={action.type}
                    onClick={() => setTempPreferences(prev => ({ ...prev, rightAction: action.type }))}
                    className={`p-3 rounded-lg border transition-all ${
                      tempPreferences.rightAction === action.type
                        ? 'border-emerald-600 bg-emerald-600/20 text-emerald-300'
                        : 'border-gray-700 hover:border-emerald-800 text-emerald-300/80 hover:text-emerald-300'
                    }`}
                  >
                    <div className="text-center">
                      <div className="text-2xl mb-1">{action.icon}</div>
                      <div className="text-xs">{action.label}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Seuil de déclenchement */}
            <div className="mb-6">
              <h3 className="text-emerald-300 font-medium mb-3">
                Sensibilité: {tempPreferences.threshold}px
              </h3>
              <input
                type="range"
                min="50"
                max="200"
                step="10"
                value={tempPreferences.threshold}
                onChange={(e) => setTempPreferences(prev => ({ 
                  ...prev, 
                  threshold: parseInt(e.target.value) 
                }))}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
              />
              <div className="flex justify-between text-xs text-emerald-300/60 mt-1">
                <span>Plus sensible</span>
                <span>Moins sensible</span>
              </div>
            </div>
          </>
        )}

        {/* Boutons d'action */}
        <div className="flex space-x-3">
          <button
            onClick={handleCancel}
            className="flex-1 px-4 py-2 border border-gray-700 rounded-lg text-emerald-300/80 hover:text-emerald-300 hover:border-emerald-800 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-white font-medium transition-colors"
          >
            Sauvegarder
          </button>
        </div>

        {/* Note d'aide */}
        <div className="mt-4 p-3 bg-emerald-900/20 border border-emerald-800/30 rounded-lg">
          <p className="text-emerald-300/80 text-xs">
            💡 <strong>Astuce:</strong> Glissez horizontalement sur les posts pour déclencher les actions rapidement. 
            Plus vous glissez loin, plus l'indicateur devient visible.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SwipePreferencesModal;
